import { paginatedTransform, rawTransform } from 'src/services/skuit/utils'
import { Audit, AuditDiff, AuditResultPlace, AuditResultSku, AuditResultUnitLoc, PaginatedResponse } from 'src/api/models'

import { API, CustomAxiosConfig } from './types'
import fileDownload from 'js-file-download'

type AuditAction =
  | 'start'
  | 'unstart'
  | 'complete'
  | 'uncomplete'
  | 'reconcile'

export interface ExportProps {
  fields: string[]
}

class PlaceAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  bulkAdd = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<null> => {
    await this.api.post(`/logistics/audits/${guid}/places`, data, config)
    return null
  }

  bulkRemove = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<null> => {
    await this.api.post(`/logistics/audits/${guid}/places/remove`, data, config)
    return null
  }

  start = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Audit>> => {
    const response = await this.api.post(`/logistics/audits/${guid}/places/${placeGuid}/start`, config)
    return paginatedTransform(Audit)(response.data)
  }

  restart = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Audit>> => {
    const response = await this.api.post(`/logistics/audits/${guid}/places/${placeGuid}/restart`, config)
    return paginatedTransform(Audit)(response.data)
  }

  complete = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<AuditResultPlace> => {
    const response = await this.api.post(`/logistics/audits/${guid}/places/${placeGuid}/complete`, config)
    return rawTransform(AuditResultPlace)(response.data)
  }

  uncomplete = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Audit>> => {
    const response = await this.api.post(`/logistics/audits/${guid}/places/${placeGuid}/uncomplete`, config)
    return paginatedTransform(Audit)(response.data)
  }

  reconcile = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Audit>> => {
    const response = await this.api.post(`/logistics/audits/${guid}/places/${placeGuid}/reconcile`, config)
    return paginatedTransform(Audit)(response.data)
  }

  diff = async (guid: string, placeGuid: string, config: CustomAxiosConfig = {}): Promise<AuditDiff> => {
    const response = await this.api.get(`/logistics/audits/${guid}/places/${placeGuid}/diff`, config)
    return response.data
  }
}

class ResultPlaceAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<AuditResultPlace>> => {
    const response = await this.api.get('/logistics/audit-result-places', config)
    return paginatedTransform(AuditResultPlace)(response.data)
  }
}

class InventoryAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<AuditResultUnitLoc>> => {
    const response = await this.api.get(`/logistics/audits/${guid}/units`, config)
    return paginatedTransform(AuditResultUnitLoc)(response.data)
  }
}

class SkuAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<AuditResultSku>> => {
    const response = await this.api.get(`/logistics/audits/${guid}/skus`, config)
    return paginatedTransform(AuditResultSku)(response.data)
  }
}

export class AuditAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  retrieve = async (slug: string, config: CustomAxiosConfig = {}): Promise<Audit> => {
    const response = await this.api.get(`/logistics/audits/${slug}`, config)
    return rawTransform(Audit)(response.data)
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Audit>> => {
    const response = await this.api.get('/logistics/audits', config)
    return paginatedTransform(Audit)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<Audit> => {
    const response = await this.api.post('/logistics/audits', data, config)
    return rawTransform(Audit)(response.data)
  }

  patch = async (slug: string, data: any, config: CustomAxiosConfig = {}): Promise<Audit> => {
    const response = await this.api.patch(`/logistics/audits/${slug}`, data, config)
    return rawTransform(Audit)(response.data)
  }

  scan = async (
    guid: string,
    data: any,
    config: CustomAxiosConfig = {}
  ): Promise<Audit> => {
    const response = await this.api.post(`/logistics/audits/${guid}/scan`, data, config)
    return rawTransform(Audit)(response.data)
  }

  action = async (
    guid: string,
    action: AuditAction,
    config: CustomAxiosConfig = {}
  ): Promise<Audit> => {
    const response = await this.api.post(`/logistics/audits/${guid}/${action}`, {}, config)
    return rawTransform(Audit)(response.data)
  }

  diff = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<AuditResultPlace>> => {
    const response = await this.api.get('/logistics/audits/diff', config)
    return paginatedTransform(AuditResultPlace)(response.data)
  }

  export = async ({ fields }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({
      url: 'logistics/audits/export',
      params,
      responseType: 'blob',
    })
    fileDownload(response.data, 'audits.csv')
  }

  get places() {
    const api = new PlaceAPI(this.api)
    Object.defineProperty(this, 'places', { value: api })
    return api
  }

  get resultPlaces() {
    const api = new ResultPlaceAPI(this.api)
    Object.defineProperty(this, 'resultPlaces', { value: api })
    return api
  }

  get skus() {
    const api = new SkuAPI(this.api)
    Object.defineProperty(this, 'skus', { value: api })
    return api
  }

  get units() {
    const api = new InventoryAPI(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }
}
